import React, { useEffect, useState } from 'react';
import './StudentDashboard.css';

interface Course {
  id: string;
  title: string;
  grade: string;
  description: string;
  duration: string;
  level: string;
  topics: string[];
  thumbnail: string;
}

interface User {
  id: number;
  email: string;
  full_name: string;
  profile_picture?: string;
  user_type: string;
  grade?: string;
}

const StudentDashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Sample courses for Class 1-10
  const courses: Course[] = [
    {
      id: 'ai-class1',
      title: 'Introduction to AI for Kids',
      grade: 'Class 1-2',
      description: 'Fun introduction to AI concepts through games and stories',
      duration: '4 weeks',
      level: 'Beginner',
      topics: ['What is AI?', 'Smart Toys', 'Voice Assistants', 'Fun with Robots'],
      thumbnail: '/images/ai-kids.jpg'
    },
    {
      id: 'ai-class3',
      title: 'AI in Everyday Life',
      grade: 'Class 3-4',
      description: 'Discover how AI helps us in daily activities',
      duration: '6 weeks',
      level: 'Beginner',
      topics: ['AI at Home', 'Smart Devices', 'Games & AI', 'Simple Coding'],
      thumbnail: '/images/ai-everyday.jpg'
    },
    {
      id: 'ai-class5',
      title: 'Introduction to Machine Learning',
      grade: 'Class 5-6',
      description: 'Learn how computers learn from data',
      duration: '8 weeks',
      level: 'Intermediate',
      topics: ['What is ML?', 'Pattern Recognition', 'Basic Python', 'Simple Projects'],
      thumbnail: '/images/ml-intro.jpg'
    },
    {
      id: 'ai-class7',
      title: 'AI & Python Programming',
      grade: 'Class 7-8',
      description: 'Build your first AI projects with Python',
      duration: '10 weeks',
      level: 'Intermediate',
      topics: ['Python Basics', 'Data Analysis', 'AI Libraries', 'Mini Projects'],
      thumbnail: '/images/python-ai.jpg'
    },
    {
      id: 'ai-class9',
      title: 'Advanced AI & Deep Learning',
      grade: 'Class 9-10',
      description: 'Master AI concepts and build real-world projects',
      duration: '12 weeks',
      level: 'Advanced',
      topics: ['Neural Networks', 'Computer Vision', 'NLP Basics', 'Portfolio Projects'],
      thumbnail: '/images/advanced-ai.jpg'
    }
  ];

  if (loading) {
    return (
      <div className="student-dashboard">
        <div className="dashboard-header">
          <div className="container">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            {user?.profile_picture && (
              <img 
                src={user.profile_picture} 
                alt={user.full_name}
                className="user-avatar"
              />
            )}
            <div className="header-text">
              <h1>Welcome back, {user?.full_name || 'Student'}! 👋</h1>
              <p>Explore courses designed for your grade level</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        <div className="container">
          {/* Progress Overview */}
          <div className="progress-overview">
            <div className="progress-card">
              <div className="progress-icon">📚</div>
              <div className="progress-info">
                <h3>0</h3>
                <p>Courses Enrolled</p>
              </div>
            </div>
            <div className="progress-card">
              <div className="progress-icon">✅</div>
              <div className="progress-info">
                <h3>0</h3>
                <p>Courses Completed</p>
              </div>
            </div>
            <div className="progress-card">
              <div className="progress-icon">🏆</div>
              <div className="progress-info">
                <h3>0</h3>
                <p>Certificates Earned</p>
              </div>
            </div>
            <div className="progress-card">
              <div className="progress-icon">⏱️</div>
              <div className="progress-info">
                <h3>0h</h3>
                <p>Learning Time</p>
              </div>
            </div>
          </div>

          {/* Available Courses */}
          <div className="courses-section">
            <h2>AI Courses for School Students</h2>
            <p className="section-subtitle">Choose a course that matches your grade level</p>
            
            <div className="courses-grid">
              {courses.map((course) => (
                <div key={course.id} className="course-card">
                  <div className="course-thumbnail">
                    <div className="course-level-badge">{course.level}</div>
                    <div className="course-grade-badge">{course.grade}</div>
                  </div>
                  
                  <div className="course-content">
                    <h3>{course.title}</h3>
                    <p className="course-description">{course.description}</p>
                    
                    <div className="course-meta">
                      <span className="course-duration">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 6v6l4 2"/>
                        </svg>
                        {course.duration}
                      </span>
                    </div>
                    
                    <div className="course-topics">
                      <h4>What you'll learn:</h4>
                      <ul>
                        {course.topics.map((topic, index) => (
                          <li key={index}>{topic}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <button className="btn-enroll">
                      Enroll for Free
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why Learn AI Section */}
          <div className="why-learn-section">
            <h2>Why Learn AI at Your Age?</h2>
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🚀</div>
                <h3>Future-Ready Skills</h3>
                <p>AI is the future. Start learning early and stay ahead</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🎮</div>
                <h3>Fun & Interactive</h3>
                <p>Learn through games, projects, and hands-on activities</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🧠</div>
                <h3>Develop Problem-Solving</h3>
                <p>Build critical thinking and logical reasoning skills</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🎓</div>
                <h3>Boost Academic Performance</h3>
                <p>AI skills complement your school subjects</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
